import React, { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import remarkGfm from "remark-gfm";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import ExportModal, { type DocumentExportData } from "~/components/Document/exportModal";
import Button from "~/components/ui/button";
import { CommonButton } from "~/components/ui/commonButton";
import { TOAST_MESSAGES } from "~/constants/messages";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import CheckWhite from "~/images/check_white.svg?react";
import EditWhite from "~/images/edit_white.svg?react";
import ExportPrimary from "~/images/export_primary.svg?react";
import type { Document } from "~/interface/document";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

interface Summary extends Document {
  sources: {
    id: string;
    type: "transcription";
  }[];
}

// 議事録コンポーネント
const SummaryContent: React.FC = () => {
  const documentApi = new DocumentApi();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryContent, setSummaryContent] = useState("");
  const [initialSummaryContent, setInitialSummaryContent] = useState("");
  const [date, setDate] = useState<Date | null>(null);
  const [initialDate, setInitialDate] = useState<Date | null>(null);
  const processLockApi = React.useMemo(() => new ProcessLockApi(), []);
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isTranscriptionEditable] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showTranscription, setShowTranscription] = useState<boolean>(false);
  const [transcriptionContent, setTranscriptionContent] = useState<string>("");
  const params = useParams();
  const navigate = useNavigate();

  // 日時をHTML5 datetime-local形式に変換
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // HTML5 datetime-localからDateオブジェクトに変換
  const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      setDate(new Date(value));
    } else {
      setDate(null);
    }
  };

  const handleUpdate = async () => {
    if (!summary?.id) {
      toast.error(TOAST_MESSAGES.SUMMARY.NOT_FOUND);
      return;
    }
    try {
      // 空文字チェック
      if (summary.name.trim() === "") {
        toast.error(TOAST_MESSAGES.SUMMARY.NAME_EMPTY);
        return;
      }

      // Update name and date if they have changed
      const updateData: Partial<{
        name: string;
        meeting: {
          date: string;
          url?: string;
        };
      }> = {};

      updateData.name = summary.name;

      if (date) {
        updateData.meeting = {
          date: date.toISOString(),
          ...(summary?.meeting?.url && { url: summary.meeting.url }),
        };
      }

      // ドキュメントのコンテンツ更新
      const response = await documentApi.updateContent(
        currentUser?.organization_id,
        currentProject.id,
        summary.id,
        {
          content: summaryContent,
        },
      );

      // メタ情報の更新
      if (Object.keys(updateData).length > 0) {
        await documentApi.updateInfo(
          currentUser?.organization_id,
          currentProject.id,
          summary.id,
          updateData,
        );
      }

      if (response !== null) {
        setIsEditing(false);
        setInitialSummaryContent(JSON.parse(JSON.stringify(summaryContent)));
        // Update summary with new modification timestamp and name/date
        setSummary((prevSummary: Summary | null) =>
          prevSummary
            ? {
                ...prevSummary,
                name: summary.name,
                ...(date && { meeting: { ...prevSummary.meeting, date: date.toISOString() } }),
                modified_at: new Date().toISOString(),
                modified_by: currentUser,
              }
            : null,
        );
      }
    } catch (_error) {
      toast.error(TOAST_MESSAGES.SUMMARY.UNKNOWN_ERROR);
    } finally {
      // エラーの有無に関わらず必ずアンロック
      try {
        await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
          type: "summary",
          mode: "edit",
        });
      } catch (unlockError) {
        console.error("アンロックエラー:", unlockError);
      }
    }
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .rs-picker-input-group {
        height: 46px !important;
        border: 1px solid #B9B6AC !important;
        border-radius: 6px !important;
      }
      .rs-picker-toggle {
        height: 46px !important;
        border: 1px solid #B9B6AC !important;
        border-radius: 6px !important;
        cursor: pointer !important;
        pointer-events: auto !important;
      }
      .rs-picker-toggle.rs-picker-toggle-active {
        border-color: #007bff !important;
      }
      .rs-picker-menu {
        z-index: 99999 !important;
        position: fixed !important;
      }
      .rs-picker-toggle-caret {
        cursor: pointer !important;
        pointer-events: auto !important;
      }
      .rs-picker-toggle-wrapper {
        pointer-events: auto !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchSummary = useCallback(async () => {
    if (!currentProject) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    if (!currentProject.outputs?.["summary"]?.id) {
      toast.error(TOAST_MESSAGES.SUMMARY.NOT_GENERATED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    let selectedId = "";
    if (params["selectedId"]) {
      selectedId = params["selectedId"];
    } else {
      toast.error(TOAST_MESSAGES.SUMMARY.NOT_SELECTED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    const documentApi = new DocumentApi();
    const response = await documentApi.get(
      currentUser.organization_id,
      currentProject.id,
      selectedId,
    );

    if (response !== null) {
      const summary = response;
      if (summary !== null) {
        // データが存在する場合
        const getSummaryContentByFormat = () => {
          try {
            // 議事録がJSON形式かをチェック
            const parsedContent = JSON.parse(summary.content);
            return parsedContent.content
              ? parsedContent.content
              : JSON.stringify(parsedContent.detail, null, 2);
          } catch (_error) {
            // JSON形式でない場合、そのままテキストとして扱う
            return summary.content;
          }
        };
        const summaryFormatted = getSummaryContentByFormat();
        const parsedSummary = summaryFormatted
          .replace(/\\n/g, "\n") // エスケープ文字を正しい改行に変換
          .replace(/\\"/g, '"') // ダブルクオートを復元
          .replace(/^"|"$/g, "") // 外側のダブルクオートを削除
          .replace(/\\n/g, "\n")
          .replace(/\\"/g, "");
        setSummaryContent(parsedSummary);
        setInitialSummaryContent(parsedSummary);
      }
      setSummary(summary as Summary);
    } else {
      toast.error(TOAST_MESSAGES.SUMMARY.FETCH_FAILED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return null;
    }
    return null;
  }, [currentProject, currentUser, params, navigate]);
  const fetchTranscriptionContent = async (): Promise<void> => {
    if (!currentProject) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    if (!currentProject.outputs?.["summary"]?.id) {
      toast.error(TOAST_MESSAGES.SUMMARY.NOT_GENERATED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }
    if (summary?.sources.length === 0) {
      setTranscriptionContent("");
    } else {
      const documentApi = new DocumentApi();
      const transcriptionId = summary?.sources[0]?.id;
      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        transcriptionId!,
      );
      if (response !== null) {
        const content = response.content;

        const getTranscriptionContentByFormat = () => {
          try {
            const parsedContent = JSON.parse(content);
            return parsedContent.detail
              ? parsedContent.detail
              : JSON.stringify(parsedContent, null, 2);
          } catch (_error) {
            return content;
          }
        };

        const transcriptionFormatted = getTranscriptionContentByFormat();
        const parsedContent = transcriptionFormatted
          .replace(/\\n/g, "\n") // Convert escape characters to proper line breaks
          .replace(/\\"/g, '"') // Restore double quotes
          .replace(/^"|"$/g, "") // Remove outer double quotes
          .replace(/\\n/g, "\n") // Second pass for nested escapes
          .replace(/\\"/g, ""); // Second pass for nested quotes
        setTranscriptionContent(parsedContent);
      } else {
        toast.error(TOAST_MESSAGES.SUMMARY.FETCH_FAILED);
        navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      }
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (summary?.meeting?.date) {
      const summaryDate = new Date(summary.meeting.date);
      setDate(summaryDate);
      setInitialDate(summaryDate);
    } else {
      // 日時が設定されていない場合は現在日時を設定
      const currentDate = new Date();
      setDate(currentDate);
      setInitialDate(currentDate);
    }
  }, [summary]);

  const handleLock = async () => {
    if (!currentProject.outputs?.["summary"]?.id) {
      toast.error(TOAST_MESSAGES.SUMMARY.NOT_FOUND);
      return;
    }

    const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
      type: "summary",
      mode: "edit",
    });

    if (response !== null) {
      setIsEditing(true);
    } else {
      toast.error(TOAST_MESSAGES.SUMMARY.LOCK_FAILED);
    }
  };

  const handleUnlock = useCallback(async () => {
    if (!currentProject.outputs?.["summary"]?.id) {
      toast.error(TOAST_MESSAGES.SUMMARY.NOT_FOUND);
      return;
    }

    await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
      type: "summary",
      mode: "edit",
    });

    setSummaryContent(JSON.parse(JSON.stringify(initialSummaryContent)));
    setDate(initialDate); // 日時も元に戻す
    // summaryの名前も元に戻す
    if (summary) {
      // 元のsummaryデータを復元するために、fetchSummaryを再実行
      fetchSummary();
    }
    setIsEditing(false);
  }, [
    currentProject,
    currentUser,
    processLockApi,
    initialSummaryContent,
    initialDate,
    summary,
    fetchSummary,
  ]);

  // 編集中の離脱時にアンロックと確認を実行
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditing) {
        // ロック解除処理
        handleUnlock();
        // カスタムメッセージ（古いブラウザでは表示される）
        event.preventDefault();
        event.returnValue = ""; // Chrome用: 空文字を指定
      }
    };

    // beforeunload イベントの登録
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // クリーンアップ
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditing, handleUnlock]);

  // 編集中の場合、ナビゲーションをブロック
  useNavigationPrompt(
    isEditing,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    handleUpdate,
    async () => {
      await handleUnlock();
    },
  );

  const handleExportClick = () => {
    if (summaryContent) {
      setShowExportModal(true);
    }
  };

  const renderMarkdown = (content: string): React.ReactNode => {
    const processedContent = content.replace(/\\n/g, "\n").replace(/\\"/g, '"');

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h1 className={styles["markdownH1"]}>{children}</h1>,
          h2: ({ children }) => <h2 className={styles["markdownH2"]}>{children}</h2>,
          h3: ({ children }) => <h3 className={styles["markdownH3"]}>{children}</h3>,
          p: ({ children }) => <p className={styles["markdownP"]}>{children}</p>,
          ul: ({ children }) => <ul className={styles["markdownUl"]}>{children}</ul>,
          ol: ({ children }) => <ol className={styles["markdownOl"]}>{children}</ol>,
          li: ({ children }) => <li className={styles["markdownLi"]}>{children}</li>,
          code: ({ children, className, ...props }) => {
            const isInline = !className || !className.includes("language-");
            return isInline ? (
              <code className={styles["markdownInlineCode"]} {...props}>
                {children}
              </code>
            ) : (
              <code className={styles["markdownCodeBlock"]} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => <pre className={styles["markdownPre"]}>{children}</pre>,
          blockquote: ({ children }) => (
            <blockquote className={styles["markdownBlockquote"]}>{children}</blockquote>
          ),
          table: ({ children }) => <table className={styles["markdownTable"]}>{children}</table>,
          thead: ({ children }) => <thead className={styles["markdownThead"]}>{children}</thead>,
          tbody: ({ children }) => <tbody className={styles["markdownTbody"]}>{children}</tbody>,
          tr: ({ children }) => <tr className={styles["markdownTr"]}>{children}</tr>,
          th: ({ children }) => <th className={styles["markdownTh"]}>{children}</th>,
          td: ({ children }) => <td className={styles["markdownTd"]}>{children}</td>,
          a: ({ children, href, ...props }) => (
            <a
              className={styles["markdownLink"]}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className={styles["markdownStrong"]}>{children}</strong>
          ),
          em: ({ children }) => <em className={styles["markdownEm"]}>{children}</em>,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSummaryContent(e.target.value);
  };

  const handleBackClick = () => {
    window.history.back();
  };

  const handleTranscriptionInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTranscriptionContent(e.target.value);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (summary) {
      setSummary({ ...summary, name: e.target.value });
    }
  };

  const handleCloseTranscription = () => {
    setShowTranscription(false);
  };
  const handleTranscriptionClick = () => {
    setShowTranscription(!showTranscription);
    fetchTranscriptionContent();
  };

  return (
    <div className={styles["wrapper"]}>
      {showExportModal && (
        <ExportModal
          documentType="summary"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "document",
            exportData: { content: summaryContent } as DocumentExportData, // ここでラップ
            name: "summary",
          }}
        />
      )}
      <span className={styles["breadcrumb-link"]} onClick={handleBackClick}>
        議事録
      </span>
      <span className={styles["breadcrumb-separator"]}>&gt;</span>
      <span className={styles["breadcrumb-detail"]}>詳細</span>
      <div className="flex justify-between items-center mb-6">
        <CommonDocumentHeader
          title={summary?.name!}
          documentType="summary"
          createdAt={summary?.created_at || ""}
          {...(summary?.modified_at && { modifiedAt: summary.modified_at })}
          {...(summary?.modified_by && { modifiedBy: summary.modified_by })}
          isEditable={isEditing}
          isLoading={false}
          isHistory={false}
          handleBackClick={handleBackClick}
          handleHistoryClick={() => {}}
        />
        <div className={styles["buttons"]}>
          {isEditing ? (
            <>
              <CommonButton icon={<CheckWhite />} onClick={handleUpdate}>
                保存
              </CommonButton>
              <CommonButton variant="label" onClick={handleUnlock}>
                キャンセル
              </CommonButton>
            </>
          ) : (
            <>
              <CommonButton icon={<EditWhite />} onClick={handleLock}>
                編集
              </CommonButton>
              <CommonButton
                icon={<ExportPrimary />}
                variant="icon"
                onClick={handleExportClick}
                disabled={isEditing}
              />
            </>
          )}
        </div>
      </div>
      <div className={styles["summary-container"]}>
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          {isEditing && (
            <div className="flex gap-3 w-full justify-between">
              <div className="flex flex-col gap-2 w-[50%]">
                <label className="mr-2 text-xs">タイトル</label>
                <input
                  value={summary?.name || ""}
                  className="border border-solid border-[#B9B6AC] rounded-md pl-3 py-3"
                  onChange={handleNameChange}
                  placeholder="議事録のタイトルを入力"
                />
              </div>
              <div className="flex flex-col gap-2 w-[50%]">
                <label className="mr-2 text-xs">打ち合わせ日時</label>
                <input
                  type="datetime-local"
                  value={formatDateForInput(date)}
                  onChange={handleNativeDateChange}
                  className={styles["dateTimeInput"]}
                  placeholder="2025-05-20 13:10"
                />
              </div>
            </div>
          )}
          <div className={styles["summary"]}>
            {isEditing ? (
              <textarea
                value={summaryContent}
                onChange={handleInputChange}
                readOnly={!isEditing}
                rows={20}
                style={{ width: "100%", fontSize: "14px", borderRadius: "4px" }}
              />
            ) : (
              <div>{renderMarkdown(summaryContent)}</div>
            )}
          </div>
        </div>
        {!showTranscription && (
          <Button
            styleType="transcription"
            label={"文字起こし"}
            onClick={handleTranscriptionClick}
            icon={"transcriptionIcon"}
          />
        )}
        {showTranscription && (
          <div className={styles["transcription"]}>
            <div className={styles["transcription-head"]}>
              <div className={styles["wrapper"]}>
                <h3>文字起こし</h3>
                {/* <Button styleType="mini" label={isTranscriptionEditable ? '編集中' : '編集'} onClick={handleEnableEditing}/> */}
              </div>
              <div className={styles["close-button"]} onClick={handleCloseTranscription}></div>
            </div>

            <textarea
              value={transcriptionContent}
              onChange={handleTranscriptionInputChange}
              rows={20}
              readOnly={!isTranscriptionEditable}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryContent;
